/** Devanagari 1-12 (matches serial on many lagna patrika / rashi charts). */
import { isValidDegreeInSign } from './jyotishaDegreeInSign';

export const RASHI_NUM_HI = ['१', '२', '३', '४', '५', '६', '७', '८', '९', '१०', '११', '१२'];

/** Lagna-chart rashi options (1 = Aries ... 12 = Pisces). */
export const LAGNA_RASHI_OPTIONS = [
  { value: 1, en: 'Aries', hi: 'मेष', vedic: 'Mesh' },
  { value: 2, en: 'Taurus', hi: 'वृषभ', vedic: 'Vrishabh' },
  { value: 3, en: 'Gemini', hi: 'मिथुन', vedic: 'Mithun' },
  { value: 4, en: 'Cancer', hi: 'कर्क', vedic: 'Kark' },
  { value: 5, en: 'Leo', hi: 'सिंह', vedic: 'Sinh' },
  { value: 6, en: 'Virgo', hi: 'कन्या', vedic: 'Kanya' },
  { value: 7, en: 'Libra', hi: 'तुला', vedic: 'Tula' },
  { value: 8, en: 'Scorpio', hi: 'वृश्चिक', vedic: 'Vrishchik' },
  { value: 9, en: 'Sagittarius', hi: 'धनु', vedic: 'Dhanu' },
  { value: 10, en: 'Capricorn', hi: 'मकर', vedic: 'Makar' },
  { value: 11, en: 'Aquarius', hi: 'कुंभ', vedic: 'Kumbh' },
  { value: 12, en: 'Pisces', hi: 'मीन', vedic: 'Meen' },
];

/** All nine grahas in traditional navagraha order for the calculator grid. */
export const NAVAGRAHA_CALC_OPTIONS = [
  { value: 'sun', labelEn: 'Sun', labelHi: 'सूर्य', symbol: '☀️', shadow: false },
  { value: 'moon', labelEn: 'Moon', labelHi: 'चंद्र', symbol: '🌙', shadow: false },
  { value: 'mars', labelEn: 'Mars', labelHi: 'मंगल', symbol: '🔥', shadow: false },
  { value: 'mercury', labelEn: 'Mercury', labelHi: 'बुध', symbol: '☿️', shadow: false },
  { value: 'jupiter', labelEn: 'Jupiter', labelHi: 'गुरु', symbol: '♃', shadow: false },
  { value: 'venus', labelEn: 'Venus', labelHi: 'शुक्र', symbol: '✨', shadow: false },
  { value: 'saturn', labelEn: 'Saturn', labelHi: 'शनि', symbol: '♄', shadow: false },
  { value: 'rahu', labelEn: 'Rahu', labelHi: 'राहु', symbol: '🐉', shadow: true },
  { value: 'ketu', labelEn: 'Ketu', labelHi: 'केतु', symbol: '☊', shadow: true },
];

/** @deprecated Use NAVAGRAHA_CALC_OPTIONS */
export const CALC_GRAHA_OPTIONS = NAVAGRAHA_CALC_OPTIONS.filter((g) => !g.shadow);

/** Teaching orbs: angular distance from Sun (degrees) for combustion / Asta. */
export const COMBUST_ORB_BY_GRAHA = {
  moon: 12,
  mars: 17,
  mercury: 13,
  jupiter: 11,
  venus: 9,
  saturn: 15,
};

/**
 * Dropdown label: number first (for patrika that print 1-12), then name.
 * @param {{ value: number; hi: string; en: string; vedic: string }} r
 */
export function formatLagnaRashiOptionLabel(r) {
  const hiNum = RASHI_NUM_HI[r.value - 1] ?? String(r.value);
  return `${r.value} (${hiNum}) · ${r.hi} · ${r.en} · ${r.vedic}`;
}

/**
 * @param {{ value: number; hi: string; en: string }} r
 */
export function formatLagnaRashiShortLabel(r) {
  const hiNum = RASHI_NUM_HI[r.value - 1] ?? String(r.value);
  return `${r.value} (${hiNum}) ${r.hi}`;
}

/**
 * @typedef {{
 *   ucchSign: number;
 *   neechSign: number;
 *   ownSigns: number[];
 *   paramUcchDegree: number | null;
 *   paramNeechDegree: number | null;
 *   shadow?: boolean;
 * }} GrahaDignityRow
 */

/** @type {Record<string, GrahaDignityRow>} */
export const GRAHA_DIGNITY_ROWS = {
  sun: { ucchSign: 1, neechSign: 7, ownSigns: [5], paramUcchDegree: 10, paramNeechDegree: 10 },
  moon: { ucchSign: 2, neechSign: 8, ownSigns: [4], paramUcchDegree: 3, paramNeechDegree: 3 },
  mars: { ucchSign: 10, neechSign: 4, ownSigns: [1, 8], paramUcchDegree: 28, paramNeechDegree: 28 },
  mercury: { ucchSign: 6, neechSign: 12, ownSigns: [3, 6], paramUcchDegree: 15, paramNeechDegree: 15 },
  jupiter: { ucchSign: 4, neechSign: 10, ownSigns: [9, 12], paramUcchDegree: 5, paramNeechDegree: 5 },
  venus: { ucchSign: 12, neechSign: 6, ownSigns: [2, 7], paramUcchDegree: 27, paramNeechDegree: 27 },
  saturn: { ucchSign: 7, neechSign: 1, ownSigns: [10, 11], paramUcchDegree: 20, paramNeechDegree: 20 },
  rahu: {
    ucchSign: 2,
    neechSign: 8,
    ownSigns: [],
    paramUcchDegree: null,
    paramNeechDegree: null,
    shadow: true,
  },
  ketu: {
    ucchSign: 8,
    neechSign: 2,
    ownSigns: [],
    paramUcchDegree: null,
    paramNeechDegree: null,
    shadow: true,
  },
};

const PARAM_ORB_DEGREES = 3;

/** @returns {Array<{ grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean; combust: boolean }>} */
export function createInitialNavagrahaEntries() {
  return NAVAGRAHA_CALC_OPTIONS.map((g) => ({
    grahaKey: g.value,
    rashiSign: null,
    degree: 0,
    retrograde: false,
    combust: false,
  }));
}

/**
 * @param {number} sign1to12
 */
export function getRashiOption(sign1to12) {
  return LAGNA_RASHI_OPTIONS.find((r) => r.value === sign1to12) ?? null;
}

/**
 * @param {string} grahaKey
 */
export function getGrahaOption(grahaKey) {
  return NAVAGRAHA_CALC_OPTIONS.find((g) => g.value === grahaKey) ?? null;
}

/**
 * @param {number} sign
 * @param {number} degree
 */
export function toSiderealLongitude(sign, degree) {
  return (sign - 1) * 30 + clampDegree(degree);
}

/**
 * @param {number} lon1
 * @param {number} lon2
 */
export function angularDistanceDegrees(lon1, lon2) {
  let d = Math.abs(lon1 - lon2);
  if (d > 180) d = 360 - d;
  return d;
}

/**
 * @param {string} grahaKey
 * @param {number} grahaSign
 * @param {number} grahaDegree
 * @param {number} sunSign
 * @param {number} sunDegree
 */
export function detectCombustFromSun(grahaKey, grahaSign, grahaDegree, sunSign, sunDegree) {
  if (grahaKey === 'sun' || grahaKey === 'rahu' || grahaKey === 'ketu') {
    return { applicable: false, combust: false, orb: null, distance: null };
  }
  const orb = COMBUST_ORB_BY_GRAHA[grahaKey];
  if (orb == null) return { applicable: false, combust: false, orb: null, distance: null };
  const dist = angularDistanceDegrees(
    toSiderealLongitude(grahaSign, grahaDegree),
    toSiderealLongitude(sunSign, sunDegree)
  );
  return { applicable: true, combust: dist <= orb, orb, distance: Math.round(dist * 10) / 10 };
}

/**
 * @param {Array<{ grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean; combust: boolean }>} entries
 */
export function countFilledNavagrahaEntries(entries) {
  return entries.filter((e) => e.rashiSign != null && e.rashiSign >= 1 && e.rashiSign <= 12).length;
}

/**
 * @param {{
 *   grahaKey: string;
 *   rashiSign: number;
 *   degree: number;
 *   retrograde?: boolean;
 *   combust?: boolean;
 *   combustFromSun?: ReturnType<typeof detectCombustFromSun> | null;
 * }} input
 */
export function evaluateGrahaDignity({ grahaKey, rashiSign, degree, retrograde = false, combust = false, combustFromSun = null }) {
  const row = GRAHA_DIGNITY_ROWS[grahaKey];
  const rashi = getRashiOption(rashiSign);
  const graha = getGrahaOption(grahaKey);

  if (!row || !rashi || !graha) {
    return {
      ok: false,
      graha,
      rashi,
      retrograde,
      combust,
      degree: clampDegree(degree),
    };
  }

  const deg = clampDegree(degree);
  const extraTags = [];
  let primary = 'sama';
  let primaryHi = 'सामान्य';
  let primaryEn = 'Regular';
  let detailHi = 'सामान्य राशि - उच्च, नीच या स्वराशि नहीं। पूरी कुंडली से बल जाँचें।';
  let detailEn = 'Regular sign - not exalted, debilitated, or own sign. Judge from full chart.';

  const isUcchSign = rashiSign === row.ucchSign;
  const isNeechSign = rashiSign === row.neechSign;
  const isOwn = row.ownSigns.includes(rashiSign) && !isUcchSign && !isNeechSign;
  const nearParamUcch = row.paramUcchDegree != null && isUcchSign && Math.abs(deg - row.paramUcchDegree) <= PARAM_ORB_DEGREES;
  const nearParamNeech = row.paramNeechDegree != null && isNeechSign && Math.abs(deg - row.paramNeechDegree) <= PARAM_ORB_DEGREES;

  if (isNeechSign) {
    if (nearParamNeech) {
      primary = 'param-neech';
      primaryHi = 'परमनीच';
      primaryEn = 'Param Neecha';
      detailHi = `परमनीच अंश के निकट (${row.paramNeechDegree}° ${rashi.hi}) - सबसे कमजोर बिंदु। नीच भंग देखें।`;
      detailEn = `Near deep debilitation (${row.paramNeechDegree}° ${rashi.en}) - weakest point. Check neecha bhanga.`;
    } else {
      primary = 'neech';
      primaryHi = 'नीच';
      primaryEn = 'Neecha';
      detailHi = `नीच राशि (${rashi.hi}) - बल कमजोर। नीच भंग और दशा से परिणाम बदल सकते हैं।`;
      detailEn = `Debilitation sign (${rashi.en}) - weaker strength. Neecha bhanga and dasha modify results.`;
    }
  } else if (isUcchSign) {
    if (nearParamUcch) {
      primary = 'param-ucch';
      primaryHi = 'परमोच्च';
      primaryEn = 'Param Uccha';
      detailHi = `परमोच्च अंश के निकट (${row.paramUcchDegree}° ${rashi.hi}) - ग्रह का सबसे बलवान बिंदु।`;
      detailEn = `Near deep exaltation (${row.paramUcchDegree}° ${rashi.en}) - peak strength zone.`;
    } else {
      primary = 'ucch';
      primaryHi = 'उच्च';
      primaryEn = 'Uccha';
      detailHi = `उच्च राशि (${rashi.hi}) - बल अधिक। भाव, दृष्टि और दशा भी देखें।`;
      detailEn = `Exaltation sign (${rashi.en}) - stronger. Also read house, aspects, dasha.`;
    }
    if (row.ownSigns.includes(rashiSign) && !nearParamUcch) {
      extraTags.push({ id: 'also-own', hi: 'स्वराशि भी', en: 'Also own sign' });
    }
  } else if (isOwn) {
    primary = 'swarashi';
    primaryHi = 'स्वराशि';
    primaryEn = 'Swa Rashi';
    detailHi = `स्वराशि (${rashi.hi}) - स्थिर बल, स्वामित्व का सहारा।`;
    detailEn = `Own sign (${rashi.en}) - steady strength from ownership.`;
  }

  if (retrograde) {
    extraTags.push({ id: 'retrograde', hi: 'वक्री', en: 'Retrograde' });
  }
  if (combust) {
    extraTags.push({ id: 'combust', hi: 'दहन (अस्त)', en: 'Combust' });
  } else if (combustFromSun?.applicable && combustFromSun.combust) {
    extraTags.push({
      id: 'combust-hint',
      hi: `सूर्य के ${combustFromSun.distance}° पर - पत्रिका पर C देखें`,
      en: `${combustFromSun.distance}° from Sun - check patrika for C`,
    });
  }

  if (row.shadow) {
    extraTags.push({
      id: 'shadow-note',
      hi: 'छाया ग्रह',
      en: 'Shadow graha',
    });
  }

  const ucchRashi = getRashiOption(row.ucchSign);
  const neechRashi = getRashiOption(row.neechSign);
  const paramUcch = row.paramUcchDegree != null ? `${row.paramUcchDegree}°` : '-';
  const paramNeech = row.paramNeechDegree != null ? `${row.paramNeechDegree}°` : '-';

  return {
    ok: true,
    graha,
    rashi,
    degree: deg,
    retrograde,
    combust,
    primary,
    primaryHi,
    primaryEn,
    detailHi,
    detailEn,
    tags: extraTags,
    nearParamUcch,
    nearParamNeech,
    reference: {
      ucchHi: ucchRashi ? `${ucchRashi.hi} (${paramUcch})` : '',
      ucchEn: ucchRashi ? `${ucchRashi.en} (${paramUcch})` : '',
      neechHi: neechRashi ? `${neechRashi.hi} (${paramNeech})` : '',
      neechEn: neechRashi ? `${neechRashi.en} (${paramNeech})` : '',
    },
  };
}

/**
 * @param {Array<{ grahaKey: string; rashiSign: number | null; degree: number; retrograde: boolean; combust: boolean }>} entries
 */
export function evaluateNavagrahaChart(entries) {
  const sunEntry = entries.find((e) => e.grahaKey === 'sun');
  const sunFilled =
    sunEntry?.rashiSign != null && sunEntry.rashiSign >= 1 && sunEntry.rashiSign <= 12;
  const sunDegreeValid = sunFilled && isValidDegreeInSign(sunEntry.degree);

  return NAVAGRAHA_CALC_OPTIONS.map((meta) => {
    const entry = entries.find((e) => e.grahaKey === meta.value);
    const filled = entry?.rashiSign != null && entry.rashiSign >= 1 && entry.rashiSign <= 12;
    const degreeValid = filled && entry != null && isValidDegreeInSign(entry.degree);
    if (!filled || !entry) {
      return { meta, entry, filled: false, degreeValid: false, result: null, combustFromSun: null };
    }
    if (!degreeValid) {
      return { meta, entry, filled: true, degreeValid: false, result: null, combustFromSun: null };
    }

    const combustFromSun =
      sunDegreeValid && meta.value !== 'sun'
        ? detectCombustFromSun(
            meta.value,
            entry.rashiSign,
            entry.degree,
            sunEntry.rashiSign,
            sunEntry.degree
          )
        : null;

    return {
      meta,
      entry,
      filled: true,
      degreeValid: true,
      combustFromSun,
      result: evaluateGrahaDignity({
        grahaKey: entry.grahaKey,
        rashiSign: entry.rashiSign,
        degree: entry.degree,
        retrograde: entry.retrograde,
        combust: entry.combust,
        combustFromSun,
      }),
    };
  });
}

/**
 * @param {number} degree
 */
function clampDegree(degree) {
  return Math.max(0, Math.min(29, Math.round(Number(degree)) || 0));
}
